import { SearchOperator, SearchPredicate, SearchPredicateDTO } from '@nx-smartmonkey/shared/domain';
import { toDayEnd } from '@nx-smartmonkey/shared/helpers';
import moment from 'moment';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import * as routing from '../../constants/Routing';
import { PlanActionType } from '../../context/plan/actions';
import { usePlanContext } from '../../context/plan/context';
import { useRetrieveActiveProject } from '../project/useRetrieveActiveProject';
import { useRouterQuery } from '../useRouterQuery';

const getDefaultSearchPredicates = () => {
  const now = new Date();
  return [
    SearchPredicate.create({
      field: `execution_date`,
      operator: `gte`,
      type: `date`,
      value: moment(now).subtract(`1`, `month`).toDate().toUTCString(),
    }),
    SearchPredicate.create({
      field: `execution_date`,
      operator: `lte`,
      type: `date`,
      value: toDayEnd(now).toUTCString(),
    }),
  ];
};

export const useSearchPlansPredicates = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { planState, planDispatch } = usePlanContext();
  const { data: activeProject } = useRetrieveActiveProject();
  const urlParams = useRouterQuery();

  useEffect(() => {
    // Only adds the predicates base64 string in the url if we are already in one of the planner plans pages.
    if (location.pathname.includes(routing.PLANNER.PLANS.replace(`:projectId`, activeProject!.id))) {
      const predicatesParam = urlParams.get(`predicates`);
      if (predicatesParam !== planState.predicatesBase64) {
        navigate(
          `${routing.PLANNER.PLANS.replace(`:projectId`, activeProject!.id)}?predicates=${planState.predicatesBase64}`
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planState.predicatesBase64]);

  useEffect(() => {
    // The first time, predicates are empty
    if (
      location.pathname.includes(routing.PLANNER.PLANS.replace(`:projectId`, activeProject!.id)) &&
      planState.predicates.length === 0
    ) {
      const predicatesParam = urlParams.get(`predicates`);

      if (predicatesParam && predicatesParam !== ``) {
        const base64Decoded = Buffer.from(predicatesParam, `base64`);
        const decoded = JSON.parse(base64Decoded.toString(`utf8`));
        if (Array.isArray(decoded) && planState.predicatesBase64 === ``) {
          const retrievedPredicates: Array<SearchPredicate> = decoded.map((sP: SearchPredicateDTO) => {
            return SearchPredicate.create({
              field: sP.field,
              operator: sP.operator as SearchOperator,
              type: sP.type,
              value: sP.value,
            });
          });

          planDispatch({
            type: PlanActionType.SET_SEARCH_PLANS_PREDICATES,
            payload: {
              predicates: retrievedPredicates,
              predicatesBase64: SearchPredicate.predicatesToBase64(retrievedPredicates),
            },
          });
          return;
        }
      }
      const defaultPredicates = getDefaultSearchPredicates();
      planDispatch({
        type: PlanActionType.SET_SEARCH_PLANS_PREDICATES,
        payload: {
          predicates: defaultPredicates,
          predicatesBase64: SearchPredicate.predicatesToBase64(defaultPredicates),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id]);

  const setPredicatesFunc = (newPredicates: Array<SearchPredicate>) => {
    const base64 = SearchPredicate.predicatesToBase64(newPredicates);
    planDispatch({
      type: PlanActionType.SET_SEARCH_PLANS_PREDICATES,
      payload: {
        predicates: newPredicates,
        predicatesBase64: base64,
      },
    });

    if (planState.predicatesBase64 !== base64) {
      navigate(`${routing.PLANNER.PLANS.replace(`:projectId`, activeProject!.id)}?predicates=${base64}`);
    } else {
      navigate(
        `${routing.PLANNER.PLANS.replace(`:projectId`, activeProject!.id)}${
          planState.predicatesBase64 ? `?predicates=${planState.predicatesBase64}` : ``
        }`
      );
    }
  };

  return {
    predicates: planState.predicates,
    predicatesBase64: planState.predicatesBase64,
    setPredicates: setPredicatesFunc,
  };
};
